import { tryParseJSON } from "@eventr/common";
import _ from "lodash";
import { isChatAvailable } from "../feat/chatpool/chat-eligibility";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { DEVTOOLS_ENABLED, GUEST_ORG_NAME } from "../consts";
import { AccessRequestSlice, createAccessRequestSlice } from "../feat/account/accessRequestsPage/accessRequest.slice";
import { create } from "../feat/appState/resetState";
import { reconcileCalendars } from "../feat/calendar/calendarProvider";
import { IChatpoolSlice, createChatpoolSlice } from "../feat/chatpool/chatpool.slice";
import { entryGenerator } from "../feat/local-notifications/schedule-local-notifications";
import { IMediaSlice, createMediaSlice } from "../feat/media/media.slice";
import { OauthSlice, createOauthSlice } from "../feat/oauth/oauth.slice";
import { ProfileSlice, createProfleSlice } from "../feat/profile/profile.slice";
import { AppEventSlice, createAppEventSlice } from "./appEvent.slice";
import { CalendarSlice, createCalendarSlice } from "./calendarState/calendar.slice";
import { IExpensesSlice, createExpensesSlice } from "./expenses.slice";
import { IFieldSlice, createFieldSlice } from "./field.slice";
import { indexDbPersistStorage } from "./indexdbStorage";
import { CurrentItinSlice } from "./itineraryState/currentItin.interfaces";
import { createCurrentItinSlice } from "./itineraryState/currentItin.slice";
import { pruneExpiredDraftSectionItems } from "./itineraryState/draft-ttl";
import { ItineraryMapSlice, createItineraryMapSlice } from "./itineraryState/itineraryMap.slice";
import { ListSlice, createListSlice } from "./list.slice";
import { LocalNotificationsSlice } from "./localNotificationsState/localNotifications.interface";
import { createLocalNotificationsSlice } from "./localNotificationsState/localNotifications.slice";
import { FilterTargetsSlice } from "./filterTargetsState/filterTargets.interface";
import { createFilterTargetsSlice } from "./filterTargetsState/filterTargets.slice";
import { OrgConfigSlice, createOrgConfigSlice } from "./orgConfig.slice";
import { RocketchatSlice } from "./rocketchat/rocketchat.interface";
import { createRocketchatSlice } from "./rocketchat/rocketchat.slice";
import { ISectionSlice, createSectionSlice } from "./section.slice";
import { useStorageStore } from "./storage/storage.store";
import { IUserSlice, createUserSlice } from "./user.slice";
import { VersionConfigSlice, createVersionConfigSlice } from "./versionConfig/versionConfig.slice";
import { ActivityFeedSlice, createActivityFeedSlice } from "./activity-feed.slice";
import { FavouritesSlice, createFavouritesSlice } from "./favourites.slice";
import { NotesSlice, createNotesSlice } from "./notes.slice";
export interface BoundStore
  extends
    OrgConfigSlice,
    IUserSlice,
    RocketchatSlice,
    ProfileSlice,
    ItineraryMapSlice,
    CalendarSlice,
    ListSlice,
    OauthSlice,
    AppEventSlice,
    AccessRequestSlice,
    ISectionSlice,
    IChatpoolSlice,
    IFieldSlice,
    IMediaSlice,
    CurrentItinSlice,
    LocalNotificationsSlice,
    FilterTargetsSlice,
    VersionConfigSlice,
    IExpensesSlice,
    ActivityFeedSlice,
    FavouritesSlice,
    NotesSlice {}

export const useBoundStore = create<BoundStore>()(
  subscribeWithSelector(
    devtools(
      persist(
        (...a) => ({
          ...createOrgConfigSlice(...a),
          ...createUserSlice(...a),
          ...createRocketchatSlice(...a),
          ...createProfleSlice(...a),
          ...createItineraryMapSlice(...a),
          ...createFavouritesSlice(...a),
          ...createNotesSlice(...a),
          ...createCurrentItinSlice(...a),
          ...createCalendarSlice(...a),
          ...createOauthSlice(...a),
          ...createAppEventSlice(...a),
          ...createAccessRequestSlice(...a),
          ...createSectionSlice(...a),
          ...createChatpoolSlice(...a),
          ...createFieldSlice(...a),
          ...createMediaSlice(...a),
          ...createListSlice(...a),
          ...createLocalNotificationsSlice(...a),
          ...createFilterTargetsSlice(...a),
          ...createVersionConfigSlice(...a),
          ...createExpensesSlice(...a),
          ...createActivityFeedSlice(...a),
        }),
        {
          name: "bound-store",
          migrate: (persistedState: unknown, version: number) => {
            console.log("Zustand Finishing Migrate Scripts");
            const state = persistedState as BoundStore;

            if (version < 2) {
              migrateV1ToV2(state);
            }

            return state;
          },
          version: 2,
          partialize: (state) =>
            /// this is to prevent some entries from being persisted
            /// derived state should not be persisted
            /// calendarPermissionGranted/calendarPermissionDenied are re-derived from the OS on every
            /// launch (checkCalInitialPermissions) - persisting them lets a stale value win the race
            /// against that check if rehydration finishes after the check does
            /// rocketWebsocket/rocketSocketStatus describe a live connection, not persistable state
            /// itineraryMapSettled says whether this session has fetched the itinerary map yet
            /// editValuesUnavailable describes this session's last editValues fetch
            /// draftSectionItems and draftSectionItemStagedAt persist deliberately; the 24h draft
            /// expiry is pruned in merge
            Object.fromEntries(
              Object.entries(state).filter(
                ([key]) =>
                  ![
                    "itineraryComponents",
                    "rocketchannelsPending",
                    "selectedItinerary",
                    "expenseFormDrafts",
                    "calendarPermissionGranted",
                    "calendarPermissionDenied",
                    "rocketWebsocket",
                    "rocketSocketStatus",
                    "itineraryMapSettled",
                    "editValuesUnavailable",
                  ].includes(key),
              ),
            ),
          storage: indexDbPersistStorage(),
          merge: (persisted, current) => {
            const p = persisted as Partial<BoundStore>;
            if (p.draftSectionItems) {
              const pruned = pruneExpiredDraftSectionItems(p.draftSectionItems, p.draftSectionItemStagedAt);
              p.draftSectionItems = pruned.drafts;
              p.draftSectionItemStagedAt = pruned.stagedAt;
            }
            return { ...current, ...p };
          },

          onRehydrateStorage: (state) => {
            console.log("Zustand Starting Rehydrate Storage");

            useStorageStore.getState().setHasHydrated("started");

            const ifBoundStoreInLocal = localStorage.getItem("bound-store");
            if (ifBoundStoreInLocal) {
              /// run migrate script...
              migrateV0ToV1(state);
            }

            return (state, error) => {
              console.log("Zustand done Rehydrate Storage");
              if (error) {
                console.error("Zustand Error Rehydrate Storage", error);
              }
              /// Drops the socket a blob written before partialize excluded it still carries.
              useBoundStore.setState({ rocketWebsocket: null, rocketSocketStatus: null });
              useStorageStore.getState().setHasHydrated("finished");
            };
          },
        },
      ),
      {
        name: "bound-store",
        enabled: DEVTOOLS_ENABLED,
      },
    ),
  ),
);

// Entry generator used for iOS widgets and local notifications.
//
// Store subscribers run synchronously inside `set()`, and this rebuild is heavy: per itinerary it
// deep-clones the record, runs ItineraryFilter, and makes native widget/notification bridge calls.
// Running it on every filter toggle blocked the toggle from repainting and dropped rapid taps, so
// it is debounced — rapid changes coalesce into one trailing run, with a `maxWait` so a continuous
// stream of changes still syncs periodically. The widget/notification feed is fine being eventually
// consistent; it never needed to update on the same tick as the toggle.
const debouncedEntryGenerator = _.debounce(entryGenerator, 400, { maxWait: 2000 });

useBoundStore.subscribe(
  (s) => {
    return {
      itineraryData: s.itineraryData,
      scheduleLocalNotifications: s.scheduleLocalNotifications,
      notificationPermissionsGranted: s.notificationPermissionsGranted,
      filterTargets: s.filterTargets,
      itineraryMap: s.itineraryMap,
    };
  },
  debouncedEntryGenerator,
  {
    equalityFn: shallow,
  },
);

// Device calendars are reconciled against the global toggle + per-itinerary calendar selection.
useBoundStore.subscribe(
  (s) => {
    return {
      itineraryData: s.itineraryData,
      calendarSyncEnabled: s.calendarSyncEnabled,
      calendarPermissionGranted: s.calendarPermissionGranted,
      filterTargets: s.filterTargets,
      itineraryMap: s.itineraryMap,
      selectedOrg: s.selectedOrg,
    };
  },
  () => {
    void reconcileCalendars();
  },
  {
    equalityFn: shallow,
  },
);

// Re-fetches RocketChat tokens when the org or its chat availability changes.
useBoundStore.subscribe(
  (s) => {
    return {
      selectedOrg: s.selectedOrg,
      orgConfig: s.orgConfig,
      getRcTokens: s.getRcTokens,
    };
  },
  (s) => {
    // Sentinel org, not isGuestMode: this can fire before guest-store has hydrated.
    if (s.selectedOrg?.name === GUEST_ORG_NAME) return;
    if (!isChatAvailable(s.orgConfig)) return;
    // The org and its config settle in quick succession at start-up; the tokens cover every
    // org, so a list fetched moments ago is still the right one.
    s.getRcTokens({ reuseWithinMs: 60_000 });
  },
  {
    equalityFn: (a, b) => {
      return _.isEqual(a.selectedOrg, b.selectedOrg) && isChatAvailable(a.orgConfig) === isChatAvailable(b.orgConfig);
    },
  },
);

// alias for convenience
export const bs = useBoundStore.getState;

/// v1 blobs predate the draft, hidden-itinerary and favourites keys; seed their defaults.
function migrateV1ToV2(currentState: BoundStore) {
  console.log("Zustand Starting Migrate Script V1>V2");
  currentState.draftSectionItemStagedAt = currentState.draftSectionItemStagedAt ?? {};
  currentState.hiddenItineraryMap = currentState.hiddenItineraryMap ?? [];
  currentState.favourites = currentState.favourites ?? {};
  console.log("Zustand Finishing Migrate Script V1>V2");
}

/// Purposely using console.log as splog not initialized at this point.
function migrateV0ToV1(currentState: BoundStore) {
  console.log("Zustand Starting Migrate Script V0>V1");
  const accessToken = localStorage.getItem("accessToken") ?? undefined;
  const refreshToken = localStorage.getItem("refreshToken") ?? undefined;
  const expiration = Number.parseInt(localStorage.getItem("expiration") ?? "0") ?? undefined;
  const profile = tryParseJSON(localStorage.getItem("profile") ?? "{}", undefined);
  const selectedItin = Number.parseInt(localStorage.getItem("selectedItinID") ?? "0");
  const wpUrl = localStorage.getItem("wp_url") ?? undefined;

  if (accessToken) {
    currentState.loginCompleted = true;
  }

  if (wpUrl) {
    const orgId = wpUrl.split("/").pop();

    if (orgId) {
      const organisations = Array.isArray(profile?.organisations) ? profile.organisations : [];
      currentState.selectedOrg = organisations.find((org: any) => org.name === orgId);
    }
  }

  currentState.IDtoken = accessToken ?? "";
  currentState.refreshToken = refreshToken ?? "";
  currentState.expiration = expiration;
  currentState.profile = profile;
  currentState.selectedItinID = selectedItin;

  const itinStore = tryParseJSON(localStorage.getItem("itinerary-store") ?? "{}");
  if (itinStore) {
    const map = itinStore.state.itineraryMap;
    for (let i = 0; i < map.length; i++) {
      const itin = map[i];
      localStorage.removeItem("ItineraryDataForID:" + itin.id);
    }
  }

  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("expiration");
  localStorage.removeItem("profile");
  localStorage.removeItem("selectedItinID");
  localStorage.removeItem("wp_url");
  localStorage.removeItem("itinerary-store");
  localStorage.removeItem("users");
  localStorage.removeItem("WP_USER");
  localStorage.removeItem("fields");
  localStorage.removeItem("sections");
  localStorage.removeItem("bound-store");

  /// reload data. 1 sec delay for initialization
  setTimeout(() => {
    bs().fetchUsers();
    bs().getProfile();
    bs().forceItineraryMapUpdate();
    bs().fetchOrgConfig();
  }, 1000);

  console.log("Zustand Finishing Migrate Script V0>V1");
}
